import React, { useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";

import _ from "lodash";

import { useAlert } from "react-alert";

import { useHistory } from "react-router";

import useCurrentEnvironment from "../../../hooks/use-environment";
import useAdminSetUpWebTrackerDomain from "../../mutations/useAdminSetUpWebTrackerDomain";

import useCurrentProjectAndEnvNames from "../../../hooks/useCurrentProjectAndEnvNames";
import validateStartingURL from "../../validateStartingURL";

import EmailsChips from "../../EmailsChips";
import useWTMURLs from "../../useWTMURLs";

const useStyles = makeStyles((theme) => ({
  emailInput: {
    position: "relative",
  },
  emailInputErrorText: {
    left: 12,
    position: "absolute",
    top: "100%",
  },
  formLabel: {
    marginBottom: theme.spacing(1),
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
}));

function SetUpDomainForm() {
  const alert = useAlert();
  const classes = useStyles();
  const currentEnvironment = useCurrentEnvironment();
  const history = useHistory();
  const wtmURLs = useWTMURLs();
  const [startingURL, setStartingURL] = useState("");
  const [emails, setEmails] = useState<string[]>([]);

  const { projName, envName } = useCurrentProjectAndEnvNames();

  const {
    mutate: setUpDomainMutation,
    isLoading: isSettingUpDomain,
  } = useAdminSetUpWebTrackerDomain(
    () => {
      alert.success("Web Tracker Monitoring successfully enabled!");

      setTimeout(() => {
        history.push(wtmURLs.trackersOverview);
      }, 1000);
    },
    (errorMessage) => alert.error(errorMessage)
  );

  const startingURLErrors = useMemo(() => {
    const errors = validateStartingURL(startingURL);

    return errors.sort().join(", ");
  }, [startingURL]);

  if (!currentEnvironment.loaded) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  const handleAddEmail = (newEmail: string) => {
    if (isSettingUpDomain) {
      return;
    }

    setEmails(_.uniq([...emails, newEmail ?? ""]));
  };

  const handleRemoveEmail = (email: string) => {
    if (isSettingUpDomain) {
      return;
    }

    setEmails(emails.filter((e) => e !== email));
  };

  const handleEnableWebTrackerMonitoring = () => {
    if (isSettingUpDomain) {
      return;
    }

    if (startingURLErrors !== "" || emails.length === 0) {
      return;
    }

    setUpDomainMutation({
      emails,
      startingURL,
    });
  };

  if (!currentEnvironment || !currentEnvironment.project) {
    return (
      <>
        "Something went wrong. Try refreshing the page, or contact
        support@freshpaint.io for assistance.";
      </>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="body1">
          Use this tool to set up Web Tracker Monitoring for this customer in
          the Freshpaint environment{" "}
          <strong>
            {projName} &gt; {envName}
          </strong>
          . Provide the starting URL for the site the customer would like to
          scan, and the email addresses we should send the weekly reports to.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1">
          Setting up Web Tracker Monitoring for a customer's site will result in
          the following changes in this Freshpaint environment:
        </Typography>
        <List component="ol">
          <ListItem>
            <ListItemText>
              1. The domain to scan will be associated with the current
              Freshpaint environment
            </ListItemText>
          </ListItem>
          <ListItem>
            <Typography variant="body1">
              2. Weekly scans for the domain will be scheduled
            </Typography>
          </ListItem>
          <ListItem>
            <Typography variant="body1">
              3. An initial scan of the domain will be started
            </Typography>
          </ListItem>
          <ListItem>
            <Typography variant="body1">
              4. The feature flag for Web Tracker Monitoring will be enabled for
              this Freshpaint environment
            </Typography>
          </ListItem>
        </List>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <FormLabel className={classes.formLabel}>
            <Typography variant="overline">Starting URL</Typography>
          </FormLabel>

          <TextField
            disabled={isSettingUpDomain}
            error={startingURLErrors !== ""}
            fullWidth
            helperText={startingURLErrors}
            placeholder="Starting URL, ie. https://www.example.com"
            required
            size="small"
            type="url"
            value={startingURL}
            onChange={(e) => {
              if (isSettingUpDomain) {
                return;
              }

              setStartingURL(e.target.value);
            }}
            variant="outlined"
          ></TextField>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <FormLabel className={classes.formLabel}>
            <Typography variant="overline">
              Emails to Send Reports To
            </Typography>
          </FormLabel>

          <EmailsChips
            disabled={isSettingUpDomain}
            emails={emails}
            onAdd={handleAddEmail}
            onDelete={handleRemoveEmail}
          />
        </FormControl>
      </Grid>

      <Divider />

      <Grid item container xs={12} justifyContent="flex-end">
        <Button
          color="primary"
          disabled={
            isSettingUpDomain || startingURLErrors !== "" || emails.length === 0
          }
          endIcon={isSettingUpDomain ? <CircularProgress size={20} /> : null}
          onClick={() => {
            handleEnableWebTrackerMonitoring();
          }}
          variant="contained"
        >
          Enable Web Tracker Monitoring
        </Button>
      </Grid>
    </Grid>
  );
}

export default SetUpDomainForm;
